import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import AppSidebar from "@/components/AppSidebar";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  ExternalLink,
  Sparkles,
  AlertTriangle,
  Settings as SettingsIcon,
  BookOpen,
  Copy,
  Check,
  Gift,
  Users,
  User,
  Bell,
  MapPin,
  FileText,
  CalendarDays,
  CreditCard,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { ChangeEmailDialog } from "@/components/settings/ChangeEmailDialog";
import { DeleteAccountDialog } from "@/components/settings/DeleteAccountDialog";
import { UsageSection } from "@/components/settings/UsageSection";
import { ProducerNotificationSettings } from "@/components/settings/ProducerNotificationSettings";
import { ProducerLocationSettings } from "@/components/settings/ProducerLocationSettings";
import { ProducerPDFSettings } from "@/components/settings/ProducerPDFSettings";
import { ProducerScheduleSettings } from "@/components/settings/ProducerScheduleSettings";
import { useProducerSettings } from "@/hooks/useProducerSettings";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/lib/utils";
import { toast as sonnerToast } from "sonner";
import { EmergencyModeSettings } from "@/components/settings/EmergencyModeSettings";

// Sections that share the standard "settings card" pattern. Emergency mode
// is rendered separately below the divider because it carries the amber
// warning styling. Keeping this as data keeps the nav rail DRY.
const SETTINGS_NAV = [
  { value: "profile", label: "Profile", icon: User },
  { value: "notifications", label: "Notifications", icon: Bell },
  { value: "location", label: "Location", icon: MapPin },
  { value: "pdf", label: "PDF export", icon: FileText },
  { value: "schedule", label: "Schedule", icon: CalendarDays },
  { value: "billing", label: "Billing", icon: CreditCard },
  { value: "referral", label: "Referral", icon: Gift },
] as const;

// Vertical nav item: full-width, left-aligned, muted until active.
const navTriggerClass =
  "w-full justify-start gap-2.5 px-3 py-2 text-muted-foreground hover:bg-muted/60 data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none";

// Referral Section Component
const ReferralSection = ({ userId }: { userId?: string }) => {
  const [copied, setCopied] = useState(false);

  // Fetch or create referral code
  const { data: referralCode, isLoading: isLoadingCode } = useQuery({
    queryKey: ["referral-code", userId],
    queryFn: async () => {
      if (!userId) return null;

      // Try to get existing code
      const { data } = await supabase
        .from("referral_codes")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (data) return data;

      // Generate a new code
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", userId)
        .single();

      const namePart = (profile?.full_name || "USER")
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .substring(0, 6);
      const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
      const code = `REF-${namePart}-${randomPart}`;

      const { data: newCode, error: insertError } = await supabase
        .from("referral_codes")
        .insert({ user_id: userId, code })
        .select()
        .single();

      if (insertError) throw insertError;
      return newCode;
    },
    enabled: !!userId,
  });

  // Fetch referrals
  const { data: referrals = [] } = useQuery({
    queryKey: ["referrals", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_user_id", userId)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: !!userId,
  });

  const completedReferrals = referrals.filter((r) => r.status === "completed");
  const pendingReferrals = referrals.filter((r) => r.status === "pending");

  const handleCopy = () => {
    if (referralCode?.code) {
      navigator.clipboard.writeText(referralCode.code);
      setCopied(true);
      sonnerToast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoadingCode) {
    return (
      <Card className="p-6">
        <Skeleton className="mb-4 h-6 w-48" />
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="mb-6 flex items-start gap-4">
          <div className="rounded-full bg-primary/10 p-3">
            <Gift className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="mb-1 text-xl font-semibold">Referral Program</h2>
            <p className="text-sm text-muted-foreground">
              Share your referral code with other producers. They get 20% off for 3 months, you get
              20% off your next invoice.
            </p>
          </div>
        </div>

        {/* Referral Code */}
        <div className="mb-6 rounded-lg bg-muted p-4">
          <Label className="mb-2 block text-xs text-muted-foreground">Your referral code</Label>
          <div className="flex items-center gap-3">
            <code className="flex-1 text-2xl font-bold tracking-wider">
              {referralCode?.code || "—"}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-muted p-3 text-center">
            <p className="text-2xl font-bold">{referrals.length}</p>
            <p className="text-xs text-muted-foreground">Total referrals</p>
          </div>
          <div className="rounded-lg bg-muted p-3 text-center">
            <p className="text-2xl font-bold">{pendingReferrals.length}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div className="rounded-lg bg-muted p-3 text-center">
            <p className="text-2xl font-bold text-primary">{completedReferrals.length}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </div>
        </div>
      </Card>

      {/* Referral list */}
      {referrals.length > 0 && (
        <Card className="p-6">
          <h3 className="mb-4 flex items-center gap-2 font-semibold">
            <Users className="h-4 w-4" />
            Referral history
          </h3>
          <div className="space-y-3">
            {referrals.map((referral) => (
              <div
                key={referral.id}
                className="flex items-center justify-between rounded-lg bg-muted p-3"
              >
                <div>
                  <p className="text-sm font-medium">Referral #{referral.id.substring(0, 8)}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(referral.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant={referral.status === "completed" ? "default" : "secondary"}>
                  {referral.status === "completed" ? "Rewarded" : "Pending"}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="border-primary/20 p-6">
        <h3 className="mb-2 font-semibold">How it works</h3>
        <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
          <li>Share your unique referral code with other producers</li>
          <li>They enter it on the upgrade page when subscribing</li>
          <li>
            They get <span className="font-medium text-foreground">20% off for 3 months</span>
          </li>
          <li>
            You get{" "}
            <span className="font-medium text-foreground">20% off on your next invoice</span>
          </li>
        </ol>
      </Card>
    </div>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { subscriptionStatus, user, isLoading: authLoading, isSubscriptionLoading } = useAuth();
  const { expanded } = useSidebar();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    settings: producerSettings,
    updateSettings,
    isLoading: settingsLoading,
  } = useProducerSettings();

  const [formData, setFormData] = useState<{
    full_name: string;
    email: string;
    company_name: string;
    phone: string;
    invoice_information: string;
  } | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/producer-login");
    }
  }, [authLoading, user, navigate]);

  // Fetch profile data
  const {
    data: profile,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !authLoading && !!user?.id,
  });

  // Handle email change success
  useEffect(() => {
    if (searchParams.get("email-change") === "success") {
      sonnerToast.success("Your email address has been updated successfully");
      setSearchParams({}); // Clear the param
    }
  }, [searchParams, setSearchParams]);

  // Refetch on mount to prevent stale data after navigation
  useEffect(() => {
    if (!authLoading && user?.id) {
      refetch();
    }
  }, [authLoading, user?.id, refetch]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (
      updates: Partial<{
        full_name: string;
        email: string;
        company_name: string;
        phone: string;
        invoice_information: string;
      }>,
    ) => {
      if (!user?.id) throw new Error("No user ID");
      const { error } = await supabase.from("profiles").update(updates).eq("id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    },
    onError: (_error) => {
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || "",
        email: profile.email || "",
        company_name: profile.company_name || "",
        phone: profile.phone || "",
        invoice_information: profile.invoice_information || "",
      });
    }
  }, [profile]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData) updateProfileMutation.mutate(formData);
  };

  const openCustomerPortal = async () => {
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to open customer portal",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  if (authLoading || !user?.id || isLoading || !profile || !formData) {
    return (
      <div className="flex min-h-screen bg-background">
        <AppSidebar />
        <main
          className="flex flex-1 items-center justify-center transition-all duration-300"
          style={{ marginLeft: expanded ? "220px" : "72px" }}
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AppSidebar />

      <main
        className="flex-1 transition-all duration-300"
        style={{ marginLeft: expanded ? "220px" : "72px" }}
      >
        <div className="w-full px-6 py-10 lg:px-10">
          <div className="mb-8 flex items-center gap-3">
            <SettingsIcon className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Settings</h1>
          </div>

          <Tabs
            defaultValue={searchParams.get("tab") || "profile"}
            orientation="vertical"
            className="flex flex-col gap-8 md:flex-row md:gap-10"
          >
            <TabsList className="h-auto w-full flex-col items-stretch justify-start gap-1 bg-transparent p-0 md:w-56 md:shrink-0">
              {SETTINGS_NAV.map(({ value, label, icon: Icon }) => (
                <TabsTrigger key={value} value={value} className={navTriggerClass}>
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </TabsTrigger>
              ))}
              <Separator className="my-1" />
              <TabsTrigger
                value="emergency"
                className={cn(
                  navTriggerClass,
                  "text-amber-500 hover:bg-amber-500/10 data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-500",
                )}
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Emergency mode
              </TabsTrigger>
            </TabsList>

            <div className="min-w-0 flex-1 md:max-w-4xl">
              <TabsContent value="profile" className="space-y-6">
                <Card className="p-6">
                  <h2 className="mb-4 text-xl font-semibold">Producer details</h2>
                  <p className="mb-6 text-sm text-muted-foreground">
                    These details will be automatically included in your production briefings (Step
                    7).
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="full_name">Producer name</Label>
                        <Input
                          id="full_name"
                          value={formData?.full_name ?? ""}
                          onChange={(e) => setFormData({ ...formData!, full_name: e.target.value })}
                          placeholder="Your name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <div className="flex gap-2">
                          <Input
                            id="email"
                            type="email"
                            value={formData?.email ?? ""}
                            disabled
                            className="flex-1 bg-muted"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setChangeEmailOpen(true)}
                          >
                            Change email
                          </Button>
                        </div>
                        {profile?.email_verified_at ? (
                          <p className="flex items-center gap-1 text-xs text-green-600">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-600"></span>
                            Email verified
                          </p>
                        ) : (
                          <p className="flex items-center gap-1 text-xs text-amber-600">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-600"></span>
                            Email not verified
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <Label>Password</Label>
                      <Link
                        to={`/auth/request-password-reset?email=${encodeURIComponent(profile?.email || "")}`}
                      >
                        <Button type="button" variant="outline">
                          Change password
                        </Button>
                      </Link>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company name</Label>
                        <Input
                          id="company_name"
                          value={formData?.company_name ?? ""}
                          onChange={(e) =>
                            setFormData({ ...formData!, company_name: e.target.value })
                          }
                          placeholder="Your company"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData?.phone ?? ""}
                          onChange={(e) => setFormData({ ...formData!, phone: e.target.value })}
                          placeholder="+31 6 12345678"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="invoice_information">Invoice information</Label>
                      <Textarea
                        id="invoice_information"
                        value={formData?.invoice_information ?? ""}
                        onChange={(e) =>
                          setFormData({ ...formData!, invoice_information: e.target.value })
                        }
                        placeholder="VAT number, company address, billing details..."
                        rows={4}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={updateProfileMutation.isPending}
                    >
                      {updateProfileMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save changes"
                      )}
                    </Button>
                  </form>
                </Card>

                {/* Onboarding Guide Section */}
                <Card className="p-6">
                  <div className="flex items-start gap-4">
                    <BookOpen className="mt-0.5 h-5 w-5 text-primary" />
                    <div className="flex-1">
                      <h3 className="mb-2 text-lg font-semibold">Onboarding guide</h3>
                      <p className="mb-4 text-sm text-muted-foreground">
                        Re-enable the onboarding guide to see tips again when navigating through the
                        dashboard and production editor.
                      </p>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={
                            !(profile as Tables<"profiles">).guide_completed &&
                            !(profile as Tables<"profiles">).briefing_guide_completed
                          }
                          onCheckedChange={async (checked) => {
                            if (!user?.id) return;
                            const updates = checked
                              ? { guide_completed: false, briefing_guide_completed: false }
                              : { guide_completed: true, briefing_guide_completed: true };
                            await supabase.from("profiles").update(updates).eq("id", user.id);
                            queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
                            queryClient.invalidateQueries({ queryKey: ["profile-guide", user.id] });
                            queryClient.invalidateQueries({
                              queryKey: ["producer-profile", user.id],
                            });
                            sonnerToast.success(checked ? "Guide re-enabled" : "Guide disabled");
                          }}
                        />
                        <Label className="text-sm">Show onboarding guide</Label>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Delete Account Section */}
                <Card className="border-destructive/50 p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
                    <div className="flex-1">
                      <h3 className="mb-2 text-lg font-semibold">Delete account</h3>
                      <p className="mb-4 text-sm text-muted-foreground">
                        This will permanently delete your PROduce account and all associated
                        productions. This action cannot be undone.
                      </p>
                      <Button variant="destructive" onClick={() => setDeleteAccountOpen(true)}>
                        Delete account
                      </Button>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="notifications">
                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <ProducerNotificationSettings
                    settings={producerSettings}
                    onUpdate={updateSettings}
                  />
                )}
              </TabsContent>

              <TabsContent value="location">
                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <ProducerLocationSettings settings={producerSettings} onUpdate={updateSettings} />
                )}
              </TabsContent>

              <TabsContent value="pdf">
                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <ProducerPDFSettings settings={producerSettings} onUpdate={updateSettings} />
                )}
              </TabsContent>

              <TabsContent value="schedule">
                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <ProducerScheduleSettings settings={producerSettings} onUpdate={updateSettings} />
                )}
              </TabsContent>

              <TabsContent value="billing" className="space-y-6">
                <UsageSection
                  planTier={subscriptionStatus?.plan_tier ?? undefined}
                  planType={subscriptionStatus?.plan_type ?? undefined}
                  planStatus={subscriptionStatus?.plan_status ?? undefined}
                  trialEndsAt={subscriptionStatus?.trial_ends_at ?? undefined}
                  isLoading={isSubscriptionLoading}
                />

                {isSubscriptionLoading ? (
                  <Card className="p-6">
                    <Skeleton className="mb-4 h-6 w-48" />
                    <Skeleton className="h-24 w-full" />
                  </Card>
                ) : (
                  <Card className="p-6">
                    <h2 className="mb-4 text-xl font-semibold">Subscription management</h2>

                    {subscriptionStatus?.plan_status === "trial" &&
                    subscriptionStatus?.plan_tier &&
                    subscriptionStatus?.trial_ends_at ? (
                      <div className="space-y-4">
                        <div className="space-y-3 rounded-lg bg-muted p-4">
                          <div>
                            <p className="mb-1 text-sm font-medium">Current plan</p>
                            <p className="text-2xl font-bold">
                              {subscriptionStatus.plan_tier.toUpperCase()}{" "}
                              <span className="text-primary">(Trial)</span>
                            </p>
                          </div>
                          <div>
                            <p className="mb-1 text-sm font-medium">Trial ends</p>
                            <p className="text-lg">
                              {new Date(subscriptionStatus.trial_ends_at).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )}
                              <span className="ml-2 text-muted-foreground">
                                (
                                {Math.max(
                                  0,
                                  Math.ceil(
                                    (new Date(subscriptionStatus.trial_ends_at).getTime() -
                                      Date.now()) /
                                      (1000 * 60 * 60 * 24),
                                  ),
                                )}{" "}
                                days remaining)
                              </span>
                            </p>
                          </div>
                        </div>
                        <Button onClick={() => navigate("/upgrade")} className="w-full">
                          Upgrade to full access
                        </Button>
                      </div>
                    ) : subscriptionStatus?.subscribed ? (
                      <div className="space-y-4">
                        <div className="space-y-3 rounded-lg bg-muted p-4">
                          <div>
                            <p className="mb-1 text-sm font-medium">Current plan</p>
                            <p className="text-2xl font-bold capitalize">
                              {subscriptionStatus.plan_tier?.toUpperCase() || ""}{" "}
                              {subscriptionStatus.plan_type === "lifetime_free"
                                ? "(Lifetime Free)"
                                : subscriptionStatus.plan_type === "monthly"
                                  ? "(Monthly)"
                                  : subscriptionStatus.plan_type === "yearly"
                                    ? "(Yearly)"
                                    : ""}
                            </p>
                          </div>

                          <div>
                            <p className="mb-1 text-sm font-medium">Price</p>
                            <p className="text-lg font-semibold">
                              {subscriptionStatus.plan_type === "lifetime_free"
                                ? "€0.00"
                                : subscriptionStatus.price
                                  ? `€${subscriptionStatus.price.toFixed(2)}${subscriptionStatus.plan_type === "monthly" ? "/month" : "/year"}`
                                  : "€0.00"}
                            </p>
                          </div>

                          {subscriptionStatus.subscription_end &&
                            subscriptionStatus.plan_type !== "lifetime_free" && (
                              <div>
                                <p className="mb-1 text-sm font-medium">Next billing date</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(subscriptionStatus.subscription_end).toLocaleDateString(
                                    "nl-NL",
                                    {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    },
                                  )}
                                </p>
                              </div>
                            )}

                          {subscriptionStatus.discount_info && (
                            <div className="border-t pt-3">
                              <p className="mb-1 text-sm font-medium">Active discount</p>
                              <p className="text-sm font-semibold text-green-600">
                                {subscriptionStatus.discount_info.code}
                              </p>
                              {subscriptionStatus.discount_info.type === "lifetime_free" ? (
                                <p className="text-xs text-muted-foreground">
                                  Lifetime free access
                                </p>
                              ) : subscriptionStatus.discount_info.type === "free_access" ? (
                                <>
                                  <p className="text-xs text-muted-foreground">
                                    Free access
                                    {subscriptionStatus.discount_info.end_date
                                      ? " until the date below"
                                      : ""}
                                  </p>
                                  {subscriptionStatus.discount_info.end_date && (
                                    <p className="text-xs text-muted-foreground">
                                      Access ends:{" "}
                                      {new Date(
                                        subscriptionStatus.discount_info.end_date,
                                      ).toLocaleDateString("nl-NL", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      })}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <p className="text-xs text-muted-foreground">
                                    {subscriptionStatus.discount_info.type === "percentage"
                                      ? `${subscriptionStatus.discount_info.value}% off`
                                      : `€${subscriptionStatus.discount_info.value} off`}
                                    {subscriptionStatus.discount_info.duration_type === "forever" &&
                                      " (Lifetime)"}
                                    {subscriptionStatus.discount_info.duration_type ===
                                      "repeating" &&
                                      ` (${subscriptionStatus.discount_info.duration_months} months)`}
                                  </p>
                                  {subscriptionStatus.discount_info.end_date && (
                                    <p className="text-xs text-muted-foreground">
                                      Expires:{" "}
                                      {new Date(
                                        subscriptionStatus.discount_info.end_date,
                                      ).toLocaleDateString("nl-NL", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      })}
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {subscriptionStatus.plan_type === "lifetime_free" ? (
                          <div className="space-y-3">
                            <p className="text-center text-xs text-muted-foreground">
                              You have lifetime free access. Want to change your plan tier?
                            </p>
                            <Button
                              onClick={() => navigate("/upgrade")}
                              variant="outline"
                              className="w-full"
                            >
                              Change plan
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <Button
                                onClick={() => navigate("/upgrade")}
                                variant="outline"
                                className="w-full"
                              >
                                <Sparkles className="mr-2 h-4 w-4" />
                                Upgrade plan
                              </Button>
                              <Button
                                onClick={openCustomerPortal}
                                disabled={isLoadingPortal}
                                className="w-full"
                              >
                                {isLoadingPortal ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Loading...
                                  </>
                                ) : (
                                  <>
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    Manage billing
                                  </>
                                )}
                              </Button>
                            </div>

                            <p className="text-center text-xs text-muted-foreground">
                              Upgrade to switch to PRO/STUDIO or yearly billing. Manage billing for
                              payment methods, invoices, or cancellation.
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-muted-foreground">
                          You don't have an active subscription. Upgrade to access all features.
                        </p>
                        <Button onClick={() => navigate("/upgrade")} className="w-full">
                          View plans
                        </Button>
                      </div>
                    )}
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="referral">
                <ReferralSection userId={user?.id} />
              </TabsContent>

              <TabsContent value="emergency">
                <EmergencyModeSettings />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </main>

      <ChangeEmailDialog
        open={changeEmailOpen}
        onOpenChange={setChangeEmailOpen}
        currentEmail={profile?.email || ""}
      />

      <DeleteAccountDialog open={deleteAccountOpen} onOpenChange={setDeleteAccountOpen} />
    </div>
  );
};

export default Settings;
